/**
 * PanicButton - the stage kill switch (SD-6).
 *
 * Big, red, instant, unguarded. One tap fires the panic (all sound/notes
 * off + reset controllers on every channel of every paired bridge, plus stop
 * every armed engine) and surfaces a summary toast. Lives in the Navigate
 * modal's header in every state (moved there 2026-09-22; performance mode
 * pins it nowhere else since DEC-216). A brief `data-firing`
 * flash confirms the tap registered even before the toast lands.
 *
 * HOLD TO LATCH (2026-09-03). Holding for `LATCH_HOLD_MS` engages the latch:
 * the rig stays dead (repeat panic + all outbound dispatch suppressed) until
 * PANIC is pressed again. Semantics and the known boundary live in
 * `performance/panicLatch.ts`.
 *
 * THE ORDERING IS LOAD-BEARING and preserves SD-6. The one-shot fires on
 * pointer DOWN, before the hold timer can possibly have elapsed, so the latch
 * cannot delay a panic by even a frame - a hold is a tap that then keeps
 * going. SD-6 chose instant-and-unguarded because a delayed real emergency is
 * unrecoverable; that property is unchanged.
 */

import { createSignal, onCleanup } from 'solid-js';
import { panic, engagePanicLatch, releasePanicLatch } from '../performance/panic.ts';
import { panicLatched, LATCH_HOLD_MS } from '../performance/panicLatch.ts';

export function PanicButton() {
  const [firing, setFiring] = createSignal(false);
  let holdTimer: ReturnType<typeof setTimeout> | null = null;
  // Set when the hold timer engages the latch, so the matching pointer-up
  // does not immediately release what the same gesture just engaged.
  let latchedByThisGesture = false;

  function clearHold() {
    if (holdTimer !== null) { clearTimeout(holdTimer); holdTimer = null; }
  }
  onCleanup(clearHold);

  function onPointerDown() {
    // Already latched: this press is the release. Do not fire another panic -
    // the rig is already silent and the operator is asking to get OUT.
    if (panicLatched()) return;

    setFiring(true);
    setTimeout(() => setFiring(false), 220);
    void panic();

    latchedByThisGesture = false;
    clearHold();
    holdTimer = setTimeout(() => {
      holdTimer = null;
      latchedByThisGesture = true;
      engagePanicLatch();
    }, LATCH_HOLD_MS);
  }

  function onPointerUp() {
    if (panicLatched() && !latchedByThisGesture) {
      releasePanicLatch();
    }
    latchedByThisGesture = false;
    clearHold();
  }

  // A pointer that leaves the button mid-hold must not latch: the operator
  // slid off, which is the standard cancel gesture on a touch surface.
  function onPointerLeave() {
    if (!panicLatched()) clearHold();
  }

  return (
    <button
      type="button"
      class="panic-button"
      data-firing={firing() ? 'true' : 'false'}
      data-latched={panicLatched() ? 'true' : 'false'}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerLeave}
      onContextMenu={(e) => e.preventDefault()}
      title={
        panicLatched()
          ? 'PANIC LOCKED - rig silenced and all sending blocked. Press to release.'
          : 'Panic - all notes/sound off on every channel of every bridge + stop every engine. Hold to lock.'
      }
      aria-label={panicLatched() ? 'Panic locked - press to release' : 'Panic - stop all MIDI and engines. Hold to lock.'}
      aria-pressed={panicLatched()}
    >
      {panicLatched() ? 'Locked' : 'Panic'}
    </button>
  );
}
